"use client";

import { useState } from "react";
import { useRouter } from "next/navigation";
import { useSession } from "next-auth/react";
import Link from "next/link";
import { Calendar, FileText, Type, Loader2 } from "lucide-react";
import ImageUpload from "@/components/common/ImageUpload";
import TagSelect from "@/components/common/TagSelect";

export default function NowaInbaPage() {
    const { data: session, status } = useSession();
    const router = useRouter();
    const [title, setTitle] = useState("");
    const [description, setDescription] = useState("");
    const [eventDate, setEventDate] = useState("");
    const [imageUrl, setImageUrl] = useState("");
    const [selectedTags, setSelectedTags] = useState<string[]>([]);
    const [error, setError] = useState("");
    const [isLoading, setIsLoading] = useState(false);

    if (status === "loading") {
        return (
            <div className="flex items-center justify-center min-h-[50vh]">
                <div className="animate-spin w-8 h-8 border-2 border-red-500 border-t-transparent rounded-full" />
            </div>
        );
    }

    if (!session) {
        return (
            <div className="container mx-auto px-4 py-16 text-center">
                <h1 className="text-2xl font-bold mb-4">Musisz być zalogowany</h1>
                <p className="text-gray-400 mb-6">
                    Aby dodać nową inbe, musisz się najpierw zalogować.
                </p>
                <Link href="/login" className="btn btn-primary">
                    Zaloguj się
                </Link>
            </div>
        );
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError("");

        if (!title.trim() || !description.trim() || !eventDate) {
            setError("Wszystkie pola są wymagane");
            return;
        }

        setIsLoading(true);

        try {
            const response = await fetch("/api/inby", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ title, description, eventDate, imageUrl, tagIds: selectedTags }),
            });

            const data = await response.json();

            if (!response.ok) {
                setError(data.error || "Nie udało się dodać inby");
            } else {
                router.push(`/inby/${data.id}`);
            }
        } catch {
            setError("Wystąpił błąd. Spróbuj ponownie.");
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="container mx-auto px-4 py-8 max-w-2xl animate-fadeIn">
            <h1 className="text-3xl font-bold mb-8">Dodaj nową inbe</h1>

            <form onSubmit={handleSubmit} className="card p-6 space-y-6">
                {error && (
                    <div className="bg-red-500/10 border border-red-500/50 text-red-400 px-4 py-3 rounded-lg text-sm">
                        {error}
                    </div>
                )}

                <div>
                    <label
                        htmlFor="title"
                        className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2"
                    >
                        <Type size={16} />
                        Tytuł inby
                    </label>
                    <input
                        id="title"
                        type="text"
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        className="input"
                        placeholder="np. Afera taśmowa"
                        maxLength={100}
                        required
                    />
                    <p className="text-xs text-gray-500 mt-1">{title.length}/100 znaków</p>
                </div>

                <div>
                    <label
                        htmlFor="description"
                        className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2"
                    >
                        <FileText size={16} />
                        Opis
                    </label>
                    <textarea
                        id="description"
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        className="input min-h-[150px] resize-y"
                        placeholder="Opisz szczegóły afery..."
                        maxLength={2000}
                        required
                    />
                    <p className="text-xs text-gray-500 mt-1">
                        {description.length}/2000 znaków
                    </p>
                </div>

                <div>
                    <label
                        htmlFor="eventDate"
                        className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2"
                    >
                        <Calendar size={16} />
                        Data wydarzenia
                    </label>
                    <input
                        id="eventDate"
                        type="date"
                        value={eventDate}
                        onChange={(e) => setEventDate(e.target.value)}
                        className="input"
                        max={new Date().toISOString().split("T")[0]}
                        required
                    />
                </div>

                <div>
                    <ImageUpload
                        value={imageUrl}
                        onChange={(url) => setImageUrl(url || "")}
                    />
                </div>

                <div>
                    <TagSelect
                        selectedIds={selectedTags}
                        onChange={setSelectedTags}
                    />
                </div>

                <div className="flex gap-4 pt-4">
                    <button
                        type="button"
                        onClick={() => router.back()}
                        className="btn btn-ghost flex-1"
                    >
                        Anuluj
                    </button>
                    <button
                        type="submit"
                        disabled={isLoading}
                        className="btn btn-primary flex-1 flex items-center justify-center gap-2"
                    >
                        {isLoading ? (
                            <>
                                <Loader2 className="animate-spin" size={18} />
                                Dodawanie...
                            </>
                        ) : (
                            "Dodaj INBĘ"
                        )}
                    </button>
                </div>
            </form>
        </div>
    );
}
